import * as apiFactory from "./ApiFactory";

import type { Api } from "./Api";
import type { PagedData } from "@/models/PagedData";
import type { AskResponse, FindResponse } from "@/models/FindAsk";
import type { CaseMetadata } from "@/models/CaseMetadata";
import type { UserCaseHistory } from "@/models/UserCaseHistory";
import type { CaseSearchBody, CaseSearchResult } from "@/models/CaseSearchResult";
import type { MigrateRequest, PlanRequest, PlanResponse } from "@/models/RecordMigration";

export async function GetUserCaseHistory(clientName: string): Promise<UserCaseHistory[]> {
  const client: Api = await apiFactory.WithBearer();
  return await client.get<UserCaseHistory[]>(["api/case/usercasehistory", clientName]);
}

export async function GetCaseMetadata(clientName: string, brand: string, caseReference: string): Promise<CaseMetadata> {
  const client: Api = await apiFactory.WithBearer();
  return await client.get<CaseMetadata>(["api/casemetadata", clientName, brand, caseReference]);
}

export async function SearchCases(
  clientName: string,
  brand: string,
  page: number,
  count: number,
  body: CaseSearchBody,
): Promise<PagedData<CaseSearchResult[]>> {
  const client: Api = await apiFactory.WithBearer();

  return await client.post<CaseSearchBody, PagedData<CaseSearchResult[]>>(
    ["api/caseSearch", clientName, brand, String(page), String(count), "/search"],
    body,
  );
}

export async function CaseSearchAutocomplete(
  clientName: string,
  brand: string,
  body: Pick<CaseSearchBody, "searchText" | "searchFields">,
): Promise<string[]> {
  const client: Api = await apiFactory.WithBearer();

  return await client.post<Pick<CaseSearchBody, "searchText" | "searchFields">, string[]>(
    ["api/caseSearch", clientName, brand, "/suggest"],
    body,
  );
}

export async function DownloadCSV(
  clientName: string,
  brand: string,
  body: CaseSearchBody & { visibleFields: string[] },
) {
  const client: Api = await apiFactory.WithBearer();
  return await client.post<typeof body, string>(["api/caseSearch", clientName, brand, "/csv"], body);
}

export async function DeleteUserCaseHistory(clientName: string, brand: string, caseReference: string): Promise<void> {
  const client = await apiFactory.WithBearer();

  await client.delete(["api/case/usercasehistory", clientName, brand, caseReference]);
}

export async function deleteCase(
  clientName: string,
  brand: string,
  caseReference: string,
  reason: string,
): Promise<void> {
  const client = await apiFactory.WithBearer();

  await client.post<Record<"reason", string>, void>(["api/case/delete", clientName, brand, caseReference], { reason });
}

export async function caseFind(clientName: string, brand: string, caseReference: string, query: string) {
  const client = await apiFactory.WithBearer();

  return await client.post<{ query: string }, FindResponse[]>(
    ["api/casesearch/Find", clientName, brand, caseReference],
    { query },
  );
}

export async function caseAsk(clientName: string, brand: string, caseReference: string, query: string) {
  const client = await apiFactory.WithBearer();

  return await client.post<{ query: string }, AskResponse>(["api/casesearch/Ask", clientName, brand, caseReference], {
    query,
  });
}

export async function caseCheckSets(clientName: string, brand: string, caseReference: string): Promise<string[]> {
  const client = await apiFactory.WithBearer();

  return await client.get<string[]>(["api/checks/sets", clientName, brand, caseReference]);
}

export async function planRecordMigration(clientName: string, brand: string, body: PlanRequest): Promise<PlanResponse> {
  const client: Api = await apiFactory.WithBearer();

  return await client.post<PlanRequest, PlanResponse>(["api/recordmigration/plan", clientName, brand], body);
}

export async function migrateRecords(clientName: string, brand: string, body: MigrateRequest): Promise<void> {
  const client: Api = await apiFactory.WithBearer();

  await client.post<MigrateRequest, void>(["api/recordmigration/migrate", clientName, brand], body);
}
